import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { departmentService } from "services";
import {
  DepartmentForm,
  DepartmentQuery,
  DepartmentUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type DepartmentListKey = ["departments"] | ["departments", DepartmentQuery];
type DepartmentDetailKey = ["departments", "detail", number | string];

const departmentKeys = {
  all: ["departments"] as const,
  list: (params?: DepartmentQuery): DepartmentListKey =>
    params ? ["departments", params] : ["departments"],
  detail: (id: number | string): DepartmentDetailKey => [
    "departments",
    "detail",
    id,
  ],
};

export function useDepartment() {
  const queryClient = useQueryClient();

  const fetchList = (params?: DepartmentQuery, enabled = true) => {
    return useQuery({
      queryKey: departmentKeys.list(params),
      queryFn: () => departmentService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: Math.max(1, data?.totalPage ?? 1),
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: departmentKeys.detail(id),
      queryFn: () => departmentService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createDepartment = useMutation({
    mutationFn: (payload: DepartmentForm) => departmentService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo phòng ban thành công");
      }
      queryClient.invalidateQueries({ queryKey: departmentKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo phòng ban thất bại");
      console.error("createDepartment failed", err);
    },
  });

  const updateDepartment = useMutation({
    mutationFn: (payload: DepartmentUpdatePayload) =>
      departmentService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Cập nhật phòng ban thành công");
      }
      queryClient.invalidateQueries({ queryKey: departmentKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật phòng ban thất bại");
      console.error("updateDepartment failed", err);
    },
  });

  const deleteDepartment = useMutation({
    mutationFn: (id: number | string) => departmentService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá phòng ban thành công");
      }
      queryClient.invalidateQueries({ queryKey: departmentKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá phòng ban thất bại");
      console.error("deleteDepartment failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createDepartment,
    updateDepartment,
    deleteDepartment,
  };
}
